import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, money, formatDate, errMsg, todayStr, confirmDelete } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { Stat, profitTone } from "@/components/Stat";
import { DataTable } from "@/components/DataTable";
import { FormDialog } from "@/components/FormDialog";
import { TxPanel } from "@/components/TxPanel";
import { JOB_FIELDS } from "@/pages/Excavation";
import { ArrowLeft, Truck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const plateLabel = (v) => `${v.plate} · ${v.brand} ${v.model}`;

export default function ExcavationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => api.get(`/excavation-jobs/${id}`).then((r) => { setD(r.data); setTick((t) => t + 1); })
    .catch(() => navigate("/hafriyat")), [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="text-muted-foreground">Yükleniyor...</div>;
  const { job: j, summary: s } = d;
  const w = can("hafriyat");
  const del = async (url, msg) => {
    try { if (await confirmDelete(url)) { toast.success(msg); load(); } } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="excavation-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/hafriyat")} className="gap-2" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Hafriyat</Button>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0"><Truck className="w-6 h-6" /></div>
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-2xl md:text-3xl" data-testid="job-title">{j.name}</h1>
            <div className="text-sm text-muted-foreground">{[j.customer_name, j.address].filter(Boolean).join(" · ")}</div>
          </div>
          <StatusBadge value={j.status} />
        </div>
        {w && (
          <div className="flex flex-wrap gap-2">
            <FormDialog title="Sefer Kaydı" triggerLabel="Sefer" triggerTestId="add-trip-btn" url="/excavation-trips" onDone={load}
              confirmKey="confirm_sold" successMsg="Sefer kaydedildi"
              initial={{ excavation_job_id: id, trip_date: todayStr(), quantity_unit: "m³", material_type: "Toprak" }}
              fields={[{ name: "vehicle_id", label: "Araç (Plaka)", type: "entity", endpoint: "/vehicles", labelKey: plateLabel, required: true },
                { name: "customer_id", label: "Müşteri Adı", type: "entity", endpoint: "/customers" },
                { name: "trip_date", label: "Tarih", type: "date" }, { name: "material_type", label: "Malzeme", type: "select", options: ["Toprak", "Kaya", "Moloz", "Kum", "Mıcır", "Diğer"] },
                { name: "loading_location", label: "Yükleme Yeri" }, { name: "unloading_location", label: "Boşaltma Yeri" },
                { name: "quantity", label: "Miktar", type: "number" }, { name: "quantity_unit", label: "Birim", type: "select", options: ["m³", "Ton", "Sefer"] },
                { name: "notes", label: "Not", type: "textarea" }]} />
            <FormDialog title="Hafriyat Gideri" triggerLabel="Gider" triggerTestId="add-exc-cost-btn" url="/excavation-costs" onDone={load}
              successMsg="Gider eklendi" initial={{ excavation_job_id: id, cost_date: todayStr(), quantity: 1 }}
              fields={[{ name: "category_id", label: "Kategori", type: "entity", endpoint: "/categories", params: { business_area: "Hafriyat", type: "Gider" }, required: true },
                { name: "vehicle_id", label: "Araç", type: "entity", endpoint: "/vehicles", labelKey: plateLabel },
                { name: "quantity", label: "Miktar", type: "number", required: true }, { name: "unit_price", label: "Birim Fiyat (₺)", type: "number", required: true },
                { name: "cost_date", label: "Tarih", type: "date" }, { name: "employee_id", label: "Personel", type: "entity", endpoint: "/employees" },
                { name: "supplier_id", label: "Tedarikçi", type: "entity", endpoint: "/suppliers" }, { name: "description", label: "Açıklama" },
                { name: "paid_now", label: "Ödendi olarak kaydet", type: "checkbox" }]} />
            <FormDialog title="İşi Düzenle" triggerLabel="Düzenle" triggerVariant="outline" icon={false} triggerTestId="edit-job-btn"
              url={`/excavation-jobs/${id}`} method="patch" onDone={load} successMsg="İş güncellendi" initial={j} confirmKey="confirm_cancel"
              transform={(x) => Object.fromEntries(JOB_FIELDS.map((f) => [f.name, x[f.name] ?? null]).filter(([, v]) => v !== null))} fields={JOB_FIELDS} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat label="Toplam Sefer" value={s.trip_count} testId="sum-trips" />
        <Stat label="Toplam Km" value={s.total_km.toLocaleString("tr-TR")} testId="sum-km" />
        <Stat label="Taşınan" value={s.total_quantity.toLocaleString("tr-TR")} hint={`${s.vehicle_count} araç`} testId="sum-qty" />
        <Stat label="Sözleşme" value={money(s.contract_amount)} tone="blue" testId="sum-contract" />
        <Stat label="Toplam Maliyet" value={money(s.total_cost)} tone="rose" testId="sum-cost" />
        <Stat label="Kâr" value={money(s.profit)} tone={profitTone(s.profit)} testId="sum-profit" />
      </div>

      <Tabs defaultValue="trips">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="trips" data-testid="tab-trips">Seferler ({d.trips.length})</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Araç Dağılımı</TabsTrigger>
          <TabsTrigger value="costs" data-testid="tab-costs">Giderler ({d.costs.length})</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance">Finansal Hareketler</TabsTrigger>
        </TabsList>
        <TabsContent value="trips"><Card className="p-4">
          <DataTable testIdPrefix="trip-row" rows={d.trips} empty="Sefer yok" columns={[
            { key: "trip_date", label: "Tarih", render: (t) => `${formatDate(t.trip_date)} ${t.departure_time || ""}` },
            { key: "plate", label: "Plaka", className: "font-mono font-semibold" }, { key: "customer_name", label: "Müşteri Adı" },
            { key: "route", label: "Güzergah", render: (t) => `${t.loading_location || "-"} → ${t.unloading_location || "-"}` },
            { key: "material_type", label: "Malzeme" },
            { key: "quantity", label: "Miktar", align: "right", render: (t) => `${t.quantity} ${t.quantity_unit}` },
            { key: "distance_km", label: "Km", align: "right" }, { key: "trip_number", label: "Günün #", align: "right" },
            { key: "x", label: "", render: (t) => w && <Button size="icon" variant="ghost" onClick={() => del(`/excavation-trips/${t.id}`, "Sefer iptal edildi")} data-testid={`archive-trip-${t.id}`}><Trash2 className="w-4 h-4" /></Button> }]} />
        </Card></TabsContent>
        <TabsContent value="vehicles"><Card className="p-4">
          <DataTable testIdPrefix="per-vehicle-row" rows={d.per_vehicle.map((v) => ({ ...v, id: v.vehicle_id }))} empty="Sefer yok"
            onRowClick={(v) => navigate(`/araclar/${v.vehicle_id}`)} columns={[
              { key: "plate", label: "Plaka", className: "font-mono font-semibold" },
              { key: "trips", label: "Sefer", align: "right", render: (v) => <b>{v.trips}</b> }, { key: "km", label: "Km", align: "right" }]} />
        </Card></TabsContent>
        <TabsContent value="costs"><Card className="p-4">
          <DataTable testIdPrefix="exc-cost-row" rows={d.costs} empty="Gider yok" columns={[
            { key: "cost_date", label: "Tarih", render: (c) => formatDate(c.cost_date) }, { key: "category_name", label: "Kategori" },
            { key: "plate", label: "Araç" }, { key: "description", label: "Açıklama" },
            { key: "quantity", label: "Miktar", align: "right" }, { key: "unit_price", label: "Birim", align: "right", render: (c) => money(c.unit_price) },
            { key: "total_amount", label: "Toplam", align: "right", render: (c) => <b>{money(c.total_amount)}</b> },
            { key: "payment_status", label: "Ödeme", render: (c) => <StatusBadge value={c.payment_status} /> },
            { key: "x", label: "", render: (c) => w && <Button size="icon" variant="ghost" onClick={() => del(`/excavation-costs/${c.id}`, "Gider iptal edildi")} data-testid={`archive-exc-cost-${c.id}`}><Trash2 className="w-4 h-4" /></Button> }]} />
        </Card></TabsContent>
        <TabsContent value="finance"><Card className="p-4"><TxPanel params={{ excavation_job_id: id }} reloadKey={tick} onChange={load} /></Card></TabsContent>
      </Tabs>
    </div>
  );
}
